import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, MapPin, Clock, Users, Tag, ChevronLeft, Minus, Plus, CreditCard, AlertCircle, Check, Phone, Mail, User } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const EventDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('pt') ? 'en' : i18n.language?.startsWith('en') ? 'en' : 'es';
  
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Customer form
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerWhatsapp, setCustomerWhatsapp] = useState('');

  useEffect(() => {
    fetchEvent();
  }, [slug]);

  const fetchEvent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/public/events/${slug}`);
      setEvent(response.data);
      if (response.data.access_levels?.length > 0) {
        setSelectedLevel(response.data.access_levels[0]);
      }
    } catch (error) {
      console.error('Error fetching event:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-MX', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getAvailability = (level) => {
    return level.capacity - (level.sold || 0);
  };

  const handleQuantityChange = (delta) => {
    const newQty = quantity + delta;
    const available = selectedLevel ? getAvailability(selectedLevel) : 0;
    if (newQty >= 1 && newQty <= Math.min(available, 10)) {
      setQuantity(newQty);
    }
  };

  const handleProceedToCheckout = () => {
    if (!selectedLevel) {
      setError('Selecciona un tipo de acceso');
      return;
    }
    const available = getAvailability(selectedLevel);
    if (quantity > available) {
      setError(`Solo hay ${available} lugares disponibles`);
      return;
    }
    setError('');
    setShowCheckout(true);
  };

  const handleCheckout = async () => {
    // Validate form
    if (!customerName.trim()) {
      setError('Ingresa tu nombre completo');
      return;
    }
    if (!customerEmail.trim() || !customerEmail.includes('@')) {
      setError('Ingresa un email válido');
      return;
    }
    if (!customerWhatsapp.trim() || customerWhatsapp.length < 10) {
      setError('Ingresa tu número de WhatsApp (mínimo 10 dígitos)');
      return;
    }

    setCheckoutLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/api/checkout/create-session`, {
        event_id: event.event_id,
        access_level_id: selectedLevel.level_id,
        quantity: quantity,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_whatsapp: customerWhatsapp,
        origin_url: window.location.origin
      });

      // Redirect to Stripe checkout
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error.response?.data?.detail || 'Error al procesar el pago. Intenta de nuevo.');
      setCheckoutLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-black pt-20 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl text-white mb-4">Evento no encontrado</h2>
          <button
            onClick={() => navigate('/eventos')}
            className="text-amber-400 hover:text-amber-300"
          >
            Volver a eventos
          </button>
        </div>
      </div>
    );
  }

  const totalPrice = selectedLevel ? selectedLevel.price * quantity : 0;

  return (
    <div className="min-h-screen bg-black pt-20">
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          onClick={() => navigate('/eventos')}
          className="flex items-center text-gray-400 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-5 h-5 mr-1" />
          Volver a eventos
        </button>
      </div>

      {/* Event Header */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative aspect-[4/3] rounded-2xl overflow-hidden"
            >
              {event.cover_image ? (
                <img 
                  src={event.cover_image} 
                  alt={event.name?.[lang] || event.name?.es}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-amber-500/20 to-amber-600/10 flex items-center justify-center">
                  <Calendar className="w-20 h-20 text-amber-500/50" />
                </div>
              )}
              {event.category && (
                <span className="absolute top-6 left-6 bg-amber-500 text-black text-sm font-bold px-4 py-2 rounded-full uppercase">
                  {event.category}
                </span>
              )}
            </motion.div>

            {/* Info */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-6"
            >
              <h1 className="text-3xl md:text-4xl font-bold text-white">
                {event.name?.[lang] || event.name?.es}
              </h1>

              <div className="space-y-4">
                <div className="flex items-center text-gray-300">
                  <Calendar className="w-5 h-5 mr-3 text-amber-500" />
                  <span className="capitalize">{formatDate(event.date)}</span>
                </div>
                
                {event.start_time && (
                  <div className="flex items-center text-gray-300">
                    <Clock className="w-5 h-5 mr-3 text-amber-500" />
                    <span>{event.start_time}{event.end_time ? ` - ${event.end_time}` : ''}</span>
                  </div>
                )}
                
                {event.venue_name && (
                  <div className="flex items-center text-gray-300">
                    <MapPin className="w-5 h-5 mr-3 text-amber-500" />
                    <div>
                      <p>{event.venue_name}</p>
                      {event.venue_address && (
                        <p className="text-gray-500 text-sm">{event.venue_address}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {event.description?.[lang] || event.description?.es ? (
                <p className="text-gray-400 leading-relaxed">
                  {event.description?.[lang] || event.description?.es}
                </p>
              ) : null}

              {/* Access Levels */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Selecciona tu acceso</h3>
                <div className="space-y-3">
                  {event.access_levels?.map((level) => {
                    const available = getAvailability(level);
                    const isSelected = selectedLevel?.level_id === level.level_id;
                    const soldOut = available <= 0;
                    
                    return (
                      <button
                        key={level.level_id}
                        onClick={() => !soldOut && setSelectedLevel(level)}
                        disabled={soldOut}
                        className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                          soldOut 
                            ? 'border-zinc-800 bg-zinc-900/50 opacity-50 cursor-not-allowed'
                            : isSelected
                              ? 'border-amber-500 bg-amber-500/10'
                              : 'border-zinc-700 bg-zinc-900 hover:border-zinc-600'
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center">
                              <span className="font-bold text-white">{level.name}</span>
                              {isSelected && !soldOut && (
                                <Check className="w-5 h-5 ml-2 text-amber-500" />
                              )}
                            </div>
                            {level.description && (
                              <p className="text-gray-500 text-sm mt-1">{level.description}</p>
                            )}
                            {level.benefits?.length > 0 && (
                              <ul className="mt-2 space-y-1">
                                {level.benefits.map((benefit, i) => (
                                  <li key={i} className="text-gray-400 text-sm flex items-center">
                                    <Check className="w-3 h-3 mr-2 text-amber-500" />
                                    {benefit}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-amber-400">
                              ${level.price.toLocaleString()}
                            </p>
                            <p className="text-gray-500 text-sm">{level.currency || 'MXN'}</p>
                            {soldOut ? (
                              <p className="text-red-400 text-sm mt-1">Agotado</p>
                            ) : (
                              <p className="text-gray-500 text-sm mt-1">{available} disponibles</p>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Quantity & Purchase */}
              {selectedLevel && getAvailability(selectedLevel) > 0 && (
                <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800 space-y-6">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-300">Cantidad</span>
                    <div className="flex items-center space-x-4">
                      <button
                        onClick={() => handleQuantityChange(-1)}
                        className="w-10 h-10 rounded-full bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 transition-colors"
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="text-xl font-bold text-white w-8 text-center">{quantity}</span>
                      <button
                        onClick={() => handleQuantityChange(1)}
                        className="w-10 h-10 rounded-full bg-zinc-800 text-white flex items-center justify-center hover:bg-zinc-700 transition-colors"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-zinc-800 pt-4">
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-gray-300">Total</span>
                      <span className="text-2xl font-bold text-amber-400">
                        ${totalPrice.toLocaleString()} {selectedLevel.currency || 'MXN'}
                      </span>
                    </div>
                    
                    <button
                      onClick={handleProceedToCheckout}
                      className="w-full py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-xl hover:from-amber-400 hover:to-amber-500 transition-all flex items-center justify-center"
                    >
                      <CreditCard className="w-5 h-5 mr-2" />
                      Comprar Acceso
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Access Rules */}
      {(event.access_rules?.[lang] || event.access_rules?.es) && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-zinc-900 rounded-xl p-6 border border-zinc-800">
              <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 text-amber-500" />
                Reglas de Acceso
              </h3>
              <p className="text-gray-400 whitespace-pre-line">
                {event.access_rules?.[lang] || event.access_rules?.es}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ID Notice */}
      <section className="pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-6">
            <p className="text-amber-400 font-medium flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              El acceso al evento requiere presentar <strong className="mx-1">identificación oficial vigente</strong> (INE, pasaporte o licencia de conducir) junto con el código QR de confirmación.
            </p>
          </div>
        </div>
      </section>

      {/* Gallery */}
      {event.gallery?.length > 0 && (
        <section className="py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h3 className="text-xl font-semibold text-white mb-6">Galería</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {event.gallery.map((img, index) => (
                <div key={index} className="aspect-square rounded-xl overflow-hidden">
                  <img src={img} alt="" className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Checkout Modal */}
      <AnimatePresence>
        {showCheckout && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowCheckout(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-zinc-900 rounded-2xl max-w-lg w-full p-6 border border-zinc-800"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-2xl font-bold text-white mb-6">Completa tu compra</h2>
              
              {/* Order Summary */}
              <div className="bg-zinc-800/50 rounded-xl p-4 mb-6">
                <p className="text-white font-medium">{event.name?.[lang] || event.name?.es}</p>
                <p className="text-gray-400 text-sm">{selectedLevel?.name} x {quantity}</p>
                <p className="text-amber-400 font-bold mt-2">Total: ${totalPrice.toLocaleString()} {selectedLevel?.currency || 'MXN'}</p>
              </div>

              {/* Customer Form */}
              <div className="space-y-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Nombre completo *</label>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="text"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Juan Pérez"
                      className="w-full bg-zinc-800 text-white pl-12 pr-4 py-3 rounded-xl border border-zinc-700 focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">Email *</label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      placeholder="tu@email.com"
                      className="w-full bg-zinc-800 text-white pl-12 pr-4 py-3 rounded-xl border border-zinc-700 focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-400 text-sm mb-2">WhatsApp *</label>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                    <input
                      type="tel"
                      value={customerWhatsapp}
                      onChange={(e) => setCustomerWhatsapp(e.target.value)}
                      placeholder="+52 55 1234 5678"
                      className="w-full bg-zinc-800 text-white pl-12 pr-4 py-3 rounded-xl border border-zinc-700 focus:border-amber-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <div className="mt-6 flex space-x-4">
                <button
                  onClick={() => setShowCheckout(false)}
                  className="flex-1 py-3 bg-zinc-800 text-white rounded-xl hover:bg-zinc-700 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading}
                  className="flex-1 py-3 bg-gradient-to-r from-amber-500 to-amber-600 text-black font-bold rounded-xl hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 flex items-center justify-center"
                >
                  {checkoutLoading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-black"></div>
                  ) : (
                    <>
                      <CreditCard className="w-5 h-5 mr-2" />
                      Pagar ${totalPrice.toLocaleString()}
                    </>
                  )}
                </button>
              </div>

              <p className="mt-4 text-center text-gray-500 text-xs">
                Pago seguro procesado por Stripe. Al continuar aceptas nuestros términos y condiciones.
              </p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default EventDetailPage;
